import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MHeader from "@/components/ui/organisms/MHeader";
import MChip from "@/components/ui/atoms/MChip";
import MDocumentCard from "@/components/ui/atoms/MDocumentCard";
import MLoader from "@/components/ui/atoms/MLoader";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import { getMemberInfo } from "@/hooks/useMembers";
import { useDocument } from "@/hooks/useDocument";
import { useScrollRestore } from "@/hooks/useScrollRestore";
import styles from "./document.module.css";
import { FileCategory, DocumentStatus } from "@/types/document.dto";
import { formatDocumentDateShort } from "@/utils/date-utils";
import DocumentDrawer from "./DocumentDrawer";
import DocumentCommentDrawer from "./DocumentCommentDrawer";
import MDocumentUploadOptions from "@/components/ui/molecules/MDocumentUploadOptions";
import { DocumentSettings } from "@/types/document-settings";

const PAGE_SIZE = 10;

const QuickAccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const isSelf = !memberInfo.onBehalf;
  const {
    fetchDocumentsWithSettings,
    downloadDocument,
    updateDocumentFlag,
    deleteDocument,
    updateDocumentNotes,
  } = useDocument(memberId);

  // Pre-select the chip with the following precedence:
  //   1. Explicit `location.state.initialChip` from DocumentsLanding's
  //      shortcut CTAs (Favorites / Offline / Private / Recent).
  //   2. Last chip the user had selected on this screen (sessionStorage).
  //      Survives the back-navigation pattern in `useBackNavigation` which
  //      strips the URL segment and re-navigates *without state*, otherwise
  //      the chip selection would always fall through to the default.
  //   3. Default to "Recent" (no filters) — a neutral landing. Previously
  //      this fell through to "Offline", which made every state-less mount
  //      look like the user had Offline selected.
  const CHIP_STORAGE_KEY = "quickAccess_lastChip";
  const initialChip = (() => {
    const fromState = (
      location.state as { initialChip?: "Favorites" | "Offline" | "Private" | "Recent" } | null
    )?.initialChip;
    if (fromState) return fromState;
    try {
      const cached = sessionStorage.getItem(CHIP_STORAGE_KEY) as
        | "Favorites"
        | "Offline"
        | "Private"
        | "Recent"
        | null;
      if (cached) return cached;
    } catch {
      // sessionStorage may be unavailable (private mode, SSR) — fall through.
    }
    return "Recent" as const;
  })();
  const initialChipFilters = (() => {
    switch (initialChip) {
      case "Favorites":
        return { isImportant: true, isPrivate: false, availableOffline: false };
      case "Private":
        return { isImportant: false, isPrivate: true, availableOffline: false };
      case "Offline":
        return { isImportant: false, isPrivate: false, availableOffline: true };
      case "Recent":
      default:
        return { isImportant: false, isPrivate: false, availableOffline: false };
    }
  })();

  const [loading, setLoading] = React.useState(true);
  const [documents, setDocuments] = React.useState<any[]>([]);
  const [documentSettings, setDocumentSettings] = React.useState<
    Map<string, DocumentSettings>
  >(new Map());
  const [chipFilters, setChipFilters] = React.useState(initialChipFilters);

  // Preserve scroll position when navigating to the document viewer and
  // back — same pattern as the main Documents listing. Refetches enough
  // pages on return to land back on the same row.
  const { saveScroll, saveItemCount, getSavedItemCount } = useScrollRestore(
    "quickAccessListing",
    loading
  );

  // If we arrived with an explicit initialChip in location.state, persist it
  // so subsequent back-navigations (which lose state) still highlight it.
  React.useEffect(() => {
    if (initialChip) {
      try {
        sessionStorage.setItem(CHIP_STORAGE_KEY, initialChip);
      } catch {
        // sessionStorage may be unavailable — non-fatal.
      }
    }
    // Run once on mount with the resolved initialChip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [drawerMode, setDrawerMode] = React.useState<
    "none" | "document" | "comment"
  >("none");
  const [selectedDoc, setSelectedDoc] = React.useState<any | null>(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [page, setPage] = React.useState(2); // next page to fetch
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [showUploadOptions, setShowUploadOptions] = React.useState(false);
  const uploadOptionsRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setDocuments([]);
        setDocumentSettings(new Map());
        setHasMore(true);
        setPage(2);

        // Build filters object with server-side filtering
        const filters: any = {
          status: {
            operation: "EQUAL",
            value: DocumentStatus.Processed,
            type: "NUMBER",
          },
        };

        // Add server-side filters based on chip selection
        if (chipFilters.availableOffline) {
          filters.availableOffline = {
            operation: "EQUAL",
            value: true,
            type: "BOOLEAN",
          };
        }
        if (chipFilters.isImportant) {
          filters.isImportant = {
            operation: "EQUAL",
            value: true,
            type: "BOOLEAN",
          };
        }
        if (chipFilters.isPrivate) {
          filters.isPrivate = {
            operation: "EQUAL",
            value: true,
            type: "BOOLEAN",
          };
        }

        const savedCount = getSavedItemCount();

        const res = (await fetchDocumentsWithSettings({
          filters,
          sort: [{ key: "documentDate", direction: "desc" }],
          page: 1,
          pageSize: PAGE_SIZE,
          pagination: { page: 1, pageSize: PAGE_SIZE },
        })) as { data: any[] };
        let list = Array.isArray(res?.data) ? res.data : [];
        let nextPage = 2;
        let more = list.length === PAGE_SIZE;

        // If returning from the viewer, refetch as many pages as the user
        // had loaded before so the scroll-restore lands on the right row.
        if (savedCount && savedCount > PAGE_SIZE) {
          while (more && list.length < savedCount && !cancelled) {
            const moreRes = (await fetchDocumentsWithSettings({
              filters,
              sort: [{ key: "documentDate", direction: "desc" }],
              page: nextPage,
              pageSize: PAGE_SIZE,
              pagination: { page: nextPage, pageSize: PAGE_SIZE },
            })) as { data: any[] };
            const chunk = Array.isArray(moreRes?.data) ? moreRes.data : [];
            if (chunk.length === 0) {
              more = false;
              break;
            }
            list = [...list, ...chunk];
            nextPage += 1;
            more = chunk.length === PAGE_SIZE;
          }
        }

        if (!cancelled && list.length > 0) {
          // Extract settings from documents into a map
          const settingsMap = new Map<string, DocumentSettings>();
          list.forEach((doc) => {
            if (doc.settings) {
              settingsMap.set(doc._id || doc.id, {
                id: "",
                documentId: doc._id || doc.id,
                memberId: memberId,
                ownerId: doc.memberId || memberId,
                ...doc.settings,
                createdAt: new Date(),
                updatedAt: new Date(),
              });
            }
          });

          setDocuments(list);
          setDocumentSettings(settingsMap);
          setHasMore(more);
          setPage(nextPage);
        } else if (!cancelled) {
          setDocuments([]);
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching quick access documents:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, chipFilters]);

  const handleChipSelect = (
    chip: "Recent" | "Offline" | "Favorites" | "Private"
  ) => {
    // Remember the active chip so back-navigation into this screen
    // (which doesn't pop history — see useBackNavigation) lands on the
    // same chip the user last selected.
    try {
      sessionStorage.setItem(CHIP_STORAGE_KEY, chip);
    } catch {
      // sessionStorage may be unavailable — non-fatal.
    }

    if (chip === "Recent") {
      setChipFilters({
        isImportant: false,
        isPrivate: false,
        availableOffline: false,
      });
    } else if (chip === "Offline") {
      setChipFilters({
        isImportant: false,
        isPrivate: false,
        availableOffline: true,
      });
    } else if (chip === "Favorites") {
      setChipFilters({
        isImportant: true,
        isPrivate: false,
        availableOffline: false,
      });
    } else if (chip === "Private") {
      setChipFilters({
        isImportant: false,
        isPrivate: true,
        availableOffline: false,
      });
    }
  };

  // No need for client-side filtering anymore - server handles it
  const filteredDocuments = React.useMemo(() => {
    return documents.filter((doc) => doc.status === DocumentStatus.Processed);
  }, [documents]);

  const handleLoadMore = React.useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      // Build filters with server-side filtering
      const filters: any = {
        status: {
          operation: "EQUAL",
          value: DocumentStatus.Processed,
          type: "NUMBER",
        },
      };

      if (chipFilters.availableOffline) {
        filters.availableOffline = {
          operation: "EQUAL",
          value: true,
          type: "BOOLEAN",
        };
      }
      if (chipFilters.isImportant) {
        filters.isImportant = {
          operation: "EQUAL",
          value: true,
          type: "BOOLEAN",
        };
      }
      if (chipFilters.isPrivate) {
        filters.isPrivate = {
          operation: "EQUAL",
          value: true,
          type: "BOOLEAN",
        };
      }

      const res = (await fetchDocumentsWithSettings({
        filters,
        sort: [{ key: "documentDate", direction: "desc" }],
        page,
        pageSize: PAGE_SIZE,
        pagination: { page, pageSize: PAGE_SIZE },
      })) as { data: any[] };
      const list = Array.isArray(res?.data) ? res.data : [];

      if (list.length > 0) {
        // Extract settings from newly loaded documents
        setDocumentSettings((prev) => {
          const newMap = new Map(prev);
          list.forEach((doc) => {
            if (doc.settings) {
              newMap.set(doc._id || doc.id, {
                id: "",
                documentId: doc._id || doc.id,
                memberId: memberId,
                ownerId: doc.memberId || memberId,
                ...doc.settings,
                createdAt: new Date(),
                updatedAt: new Date(),
              });
            }
          });
          return newMap;
        });

        setDocuments((prev) => [...prev, ...list]);
        setHasMore(list.length === PAGE_SIZE);
        setPage((prev) => prev + 1);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error loading more quick access documents:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchDocumentsWithSettings, hasMore, isLoadingMore, page, memberId]);

  const emptyStateTitle = React.useMemo(() => {
    if (chipFilters.availableOffline) {
      return "No documents are saved for offline access yet";
    }
    if (chipFilters.isImportant) {
      return "You haven't added any favourites yet";
    }
    if (chipFilters.isPrivate) {
      return "You haven't marked any documents as private yet";
    }
    return "No documents match your filters";
  }, [chipFilters]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <MLoader size={40} />
      </div>
    );
  }

  return (
    <div style={{ padding: "0 24px", background: "#fff" }}>
      <MHeader
        subHeading="Shortcuts"
        module="documents"
        memberInfo={memberInfo}
        showProfilePickerRow
        showGradient
        onAdd={() => setShowUploadOptions(true)}
        description={
          isSelf
            ? "Quickly find important documents that you've marked as Offline, Favourite, or Private for yourself."
            : "If you've marked any document for this member as offline or favourite, it will show up here."
        }
        learnMoreContent={
          <>
            Private — Files only you can see. These aren’t visible to other
            family members.
            <br />
            <br />
            Offline — Files that stay available even without internet access.
            <br />
            <br />
            Favourites — Important or frequently used documents you want to
            reach quickly.
          </>
        }
      />

      <div className={styles.chipsContainer} style={{ marginTop: 16 }}>
        <MChip
          text="Favorites"
          variant="documentChip"
          rightIcon={false}
          selected={chipFilters.isImportant}
          onClick={() => handleChipSelect("Favorites")}
        />
        <MChip
          text="Offline"
          variant="documentChip"
          rightIcon={false}
          selected={chipFilters.availableOffline}
          onClick={() => handleChipSelect("Offline")}
        />
        {!memberInfo.onBehalf && (
          <MChip
            text="Private"
            variant="documentChip"
            rightIcon={false}
            selected={chipFilters.isPrivate}
            onClick={() => handleChipSelect("Private")}
          />
        )}
      </div>

      {filteredDocuments.length > 0 ? (
        <div
          className={styles.documentCardsContainer}
          style={{ marginTop: 24 }}
        >
          <MInfiniteScroll
            data={filteredDocuments}
            hasMore={hasMore}
            isLoading={isLoadingMore}
            onLoadMore={handleLoadMore}
            renderItem={(doc, idx) => (
              <MDocumentCard
                className={styles.quickAccessCard}
                cardType="document"
                category={doc.category}
                key={doc._id || doc.id || idx}
                displayName={doc.displayName || `Document ${idx + 1}`}
                meta={
                  doc.documentDate
                    ? formatDocumentDateShort(doc.documentDate)
                    : ""
                }
                showComment={doc.notes}
                isPrivate={doc.isPrivate || false}
                isImportant={
                  documentSettings.get(doc._id || doc.id)?.isImportant || false
                }
                availableOffline={
                  documentSettings.get(doc._id || doc.id)?.availableOffline ||
                  false
                }
                insights={doc.insights || false}
                documentId={doc._id || doc.id}
                onOptionsClick={() => {
                  setDrawerMode("document");
                  setSelectedDoc(doc);
                }}
                onViewDocument={() => {
                  saveScroll();
                  saveItemCount(filteredDocuments.length);
                  if (doc.category === FileCategory.EmergencyCard) {
                    navigate("/dashboard/emergency-card", {
                      state: { backUrl: "/documents/quick-access" },
                    });
                  } else {
                    navigate("/documents/quick-access/view", {
                      state: { document: doc },
                    });
                  }
                }}
              />
            )}
          />
        </div>
      ) : (
        <div style={{ marginTop: 32, background: "#fff" }}>
          <MEmptyCard primaryTitle={emptyStateTitle} />
        </div>
      )}

      {drawerMode === "document" && selectedDoc && (
        <DocumentDrawer
          open={true}
          onClose={() => setDrawerMode("none")}
          onUpdate={async () => {
            // Refresh documents with settings (keep drawer open)
            const res = (await fetchDocumentsWithSettings({
              filters: {
                status: {
                  operation: "EQUAL",
                  value: DocumentStatus.Processed,
                  type: "NUMBER",
                },
              },
              sort: [{ key: "documentDate", direction: "desc" }],
              page: 1,
              pageSize: PAGE_SIZE,
              pagination: { page: 1, pageSize: PAGE_SIZE },
            })) as { data: any[] };
            const list = Array.isArray(res?.data) ? res.data : [];

            if (list.length > 0) {
              // Extract settings from documents
              const settingsMap = new Map<string, DocumentSettings>();
              list.forEach((doc) => {
                if (doc.settings) {
                  settingsMap.set(doc._id || doc.id, {
                    id: "",
                    documentId: doc._id || doc.id,
                    memberId: memberId,
                    ownerId: doc.memberId || memberId,
                    ...doc.settings,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  });
                }
              });
              setDocumentSettings(settingsMap);
            }

            setDocuments(list);
            setHasMore(list.length === PAGE_SIZE);
            setPage(2);
          }}
          onDocumentChange={(updatedDoc) => {
            // Update the document in the documents array without full refresh
            setDocuments((prev) =>
              prev.map((doc) =>
                (doc._id || doc.id) === (updatedDoc._id || updatedDoc.id)
                  ? { ...doc, ...updatedDoc }
                  : doc
              )
            );
            // Also update selectedDoc so drawer shows updated values - create new object reference
            setSelectedDoc((prev) => ({ ...prev, ...updatedDoc }));
          }}
          displayName={selectedDoc.displayName || "Document"}
          fileKey={selectedDoc.media?.fileKey}
          onDelete={async () => {
            await deleteDocument(selectedDoc, setDocuments);
            setDrawerMode("none");
          }}
          onCommentClick={() => setDrawerMode("comment")}
          commentButtonText={selectedDoc.notes ? "Edit Comment" : "Add Comment"}
          category={selectedDoc.category}
          document={selectedDoc}
        />
      )}

      {drawerMode === "comment" && selectedDoc && (
        <DocumentCommentDrawer
          open={true}
          onClose={() => setDrawerMode("none")}
          initialValue={selectedDoc.notes || ""}
          loading={false}
          onDone={async (notes) => {
            await updateDocumentNotes(selectedDoc, notes, setDocuments);
            setSelectedDoc((doc) => ({ ...doc, notes }));
            setDrawerMode("none");
          }}
          title={`${selectedDoc.notes ? "Edit Comment" : "Add Comment"}`}
        />
      )}

      <MDocumentUploadOptions
        ref={uploadOptionsRef}
        open={showUploadOptions}
        onClose={() => setShowUploadOptions(false)}
      />
    </div>
  );
};

export default QuickAccess;
